<template>
  <div class="color-picker">

    <div>
    <div class="recent-colors-title">Theme Colors</div></div>


    <div class="picker-presets" style="margin-top: 25px">
      <div
          class="picker-gradient-col"
          v-for="(c, index) in theme.themeColors"
          :key="index"
      >
        <div class="picker-gradient-color"
             :style="{ background: c }"
             @click="selectThemeColor(index)"
        ></div>
      </div>


        <Button
            icon="pi pi-pencil"
            rounded
            outlined
            aria-label="Edit Theme"
            @click="emit('editTheme'); themeDialogVisible = true"
            style="top: -16px;"
        />

    </div>

    <div class="recent-colors-title">Default Colors</div>
    <div class="picker-presets">
      <div
        class="picker-gradient-col"
        v-for="(c, index) in presetColors"
        :key="index"
      >
        <div class="picker-gradient-color"
          :style="{ background: c, border: c === '#ffffff' ? '1px solid lightgray' : 'none' }"
          @click="selectPresetColor(c)"
        ></div>
      </div>
    </div>

    <div style="width: 200px; height: 200px">
      <div class="picker-saturation-wrap">
        <Saturation :value="color" :hue="hue" @colorChange="value => changeColor(value)" />
      </div>
      <div class="picker-controls">
        <div class="picker-color-wrap">
          <div class="picker-current-color" :style="{ background: currentColor }"></div>
          <Checkboard />
        </div>
        <div class="picker-sliders">
          <div class="picker-hue-wrap">
            <Hue :value="color" :hue="hue" @colorChange="value => changeColor(value)" />
          </div>
          <div class="picker-alpha-wrap">
            <Alpha :value="color" @colorChange="value => changeColor(value)" />
          </div>
        </div>
      </div>

      <div class="picker-field">
        <EditableInput class="input" :value="color" @colorChange="value => changeColor(value)" />
        <div class="straw" @click="openEyeDropper()"><IconNeedle /></div>
        <div class="transparent" @click="selectPresetColor('#00000000')">
          <Checkboard />
        </div>
      </div>
    </div>




  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import tinycolor, { ColorFormats } from 'tinycolor2'
import { debounce } from 'lodash'
import { toCanvas } from 'html-to-image'
import { useSlidesStore, useMainStore} from "@/store";

import Alpha from './Alpha.vue'
import Checkboard from './Checkboard.vue'
import Hue from './Hue.vue'
import Saturation from './Saturation.vue'
import EditableInput from './EditableInput.vue'

import { message } from 'ant-design-vue'
import Button from "primevue/button";
import {storeToRefs} from "pinia";

const model = defineModel()
const { themeDialogVisible } = storeToRefs(useMainStore())
const { theme } = storeToRefs(useSlidesStore())

const emit = defineEmits<{
  (event: 'update:modelValue', payload: string): void
  (event: 'colorSelected', payload: string): void
  (event: 'editTheme'): void
}>()

const RECENT_COLORS = 'RECENT_COLORS'

const getPresetColors = () => {
  return [
    '#000000',
    '#545454',
    '#737373',
    '#a6a6a6',
    '#d9d9d9',
    '#ffffff',

    '#ff3131',
    '#ff5757',
    '#ff66c4',
    '#cb6ce6',
    '#8c52ff',
    '#5e17eb',

    '#0097b2',
    '#0cc0df',
    '#5ce1e6',
    '#38b6ff',
    '#5271ff',
    '#004aad',

    '#00bf63',
    '#7ed957',
    '#c1ff72',
    '#ffde59',
    '#ffbd59',
    '#ff914d'
  ]
}

const hue = ref(-1)
const recentColors = ref<string[]>([])

const editTheme = () => {
  themeDialogVisible.value = true
}

const color = computed({
  get() {
    return tinycolor(model.value).toRgb()
  },
  set(rgba: ColorFormats.RGBA) {
    const rgbaString = `rgba(${[rgba.r, rgba.g, rgba.b, rgba.a].join(',')})`
    emit('update:modelValue', rgbaString)
    //emit('colorSelected', rgbaString)
  },
})

const presetColors = getPresetColors()

const currentColor = computed(() => {
  return `rgba(${[color.value.r, color.value.g, color.value.b, color.value.a].join(',')})`
})

const selectPresetColor = (colorString: string) => {
  hue.value = tinycolor(colorString).toHsl().h
  model.value = colorString
  emit('colorSelected', colorString)
}

const selectThemeColor = (colorIndex: number) => {
  model.value = `var(--theme-${colorIndex})`
  emit('colorSelected', `var(--theme-${colorIndex})`)
}

// Every time you select a non-preset color, you need to add the color to the recently used list
const updateRecentColorsCache = debounce(function() {
  const _color = tinycolor(color.value).toRgbString()
  if (!recentColors.value.includes(_color)) {
    recentColors.value = [_color, ...recentColors.value]

    const maxLength = 10
    if (recentColors.value.length > maxLength) {
      recentColors.value = recentColors.value.slice(0, maxLength)
    }
  }
}, 300, { trailing: true })

onMounted(() => {
  const recentColorsCache = localStorage.getItem(RECENT_COLORS)
  if (recentColorsCache) recentColors.value = JSON.parse(recentColorsCache)
})

watch(recentColors, () => {
  const recentColorsCache = JSON.stringify(recentColors.value)
  localStorage.setItem(RECENT_COLORS, recentColorsCache)
})

const changeColor = (value: ColorFormats.RGBA | ColorFormats.HSLA | ColorFormats.HSVA) => {
  if ('h' in value) {
    hue.value = value.h
    if (value.a === 0) value.a = 1
    color.value = tinycolor(value).toRgb()
  }
  else {
    hue.value = tinycolor(value).toHsl().h
    color.value = value
  }

  updateRecentColorsCache()

}

// Open the eyedropper
// Check whether the environment supports the native color picker, if supported, use the native eyedropper, otherwise use the custom eyedropper
const openEyeDropper = () => {
  const isSupportedEyeDropper = 'EyeDropper' in window

  if (isSupportedEyeDropper) browserEyeDropper()
  else customEyeDropper()
}

// Native color picker
const browserEyeDropper = () => {
  message.success('Press the ESC key to close the eyedropper')

  // eslint-disable-next-line
  const eyeDropper = new (window as any).EyeDropper()
  eyeDropper.open().then((result: { sRGBHex: string }) => {
    const tColor = tinycolor(result.sRGBHex)
    hue.value = tColor.toHsl().h
    color.value = tColor.toRgb()

    updateRecentColorsCache()
  }).catch(() => {
    message.success('Close eyedropper')
  })
}

// Canvas-based custom color picker
const customEyeDropper = () => {
  const targetRef: HTMLElement | null = document.querySelector('.canvas')
  if (!targetRef) return

  const maskRef = document.createElement('div')
  maskRef.style.cssText = 'position: fixed; top: 0; left: 0; bottom: 0; right: 0; z-index: 9999; cursor: wait;'
  document.body.appendChild(maskRef)

  const colorBlockRef = document.createElement('div')
  colorBlockRef.style.cssText = 'position: absolute; top: -100px; left: -100px; width: 16px; height: 16px; border: 1px solid #000; z-index: 999'
  maskRef.appendChild(colorBlockRef)

  const { left, top, width, height } = targetRef.getBoundingClientRect()

  const filter = (node: HTMLElement) => {
    if (node.tagName && node.tagName.toUpperCase() === 'FOREIGNOBJECT') return false
    if (node.classList && node.classList.contains('operate')) return false
    return true
  }

  toCanvas(targetRef, { filter, fontEmbedCSS: '', width, height, canvasWidth: width, canvasHeight: height, pixelRatio: 1 }).then(canvasRef => {
    canvasRef.style.cssText = `position: absolute; top: ${top}px; left: ${left}px; cursor: crosshair;`
    maskRef.style.cursor = 'default'
    maskRef.appendChild(canvasRef)

    const ctx = canvasRef.getContext('2d')
    if (!ctx) return

    let currentColor = ''
    const handleMousemove = (e: MouseEvent) => {
      const x = e.x
      const y = e.y

      const mouseX = x - left
      const mouseY = y - top

      const [r, g, b, a] = ctx.getImageData(mouseX, mouseY, 1, 1).data
      currentColor = `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(2)})`

      colorBlockRef.style.left = x + 10 + 'px'
      colorBlockRef.style.top = y + 10 + 'px'
      colorBlockRef.style.backgroundColor = currentColor
    }
    const handleMouseleave = () => {
      currentColor = ''
      colorBlockRef.style.left = '-100px'
      colorBlockRef.style.top = '-100px'
      colorBlockRef.style.backgroundColor = ''
    }
    const handleMousedown = (e: MouseEvent) => {
      if (currentColor && e.button === 0) {
        const tColor = tinycolor(currentColor)
        hue.value = tColor.toHsl().h
        color.value = tColor.toRgb()

        updateRecentColorsCache()
      }
      document.body.removeChild(maskRef)

      canvasRef.removeEventListener('mousemove', handleMousemove)
      canvasRef.removeEventListener('mouseleave', handleMouseleave)
      window.removeEventListener('mousedown', handleMousedown)
    }

    canvasRef.addEventListener('mousemove', handleMousemove)
    canvasRef.addEventListener('mouseleave', handleMouseleave)
    window.addEventListener('mousedown', handleMousedown)
  }).catch(() => {
    message.error('The color picker failed to initialize')
    document.body.removeChild(maskRef)
  })
}
</script>

<style lang="scss" scoped>
.color-picker {
  position: relative;
  width: 510px;
  background: #fff;
  user-select: none;
  margin-bottom: -10px;
}
.picker-saturation-wrap {
  width: 200px;
  padding-bottom: 100px;
  position: relative;
  overflow: hidden;
}
.picker-controls {
  display: flex;
}
.picker-sliders {
  padding: 4px 0;
  flex: 1;
}
.picker-hue-wrap {
  position: relative;
  height: 10px;
}
.picker-alpha-wrap {
  position: relative;
  height: 10px;
  margin-top: 4px;
  overflow: hidden;
}
.picker-color-wrap {
  width: 24px;
  height: 24px;
  position: relative;
  margin-top: 4px;
  margin-right: 4px;
  outline: 1px dashed rgba($color: #666, $alpha: .12);

  .checkerboard {
    background-size: auto;
  }
}
.picker-current-color {
  @include absolute-0();

  z-index: 2;
}

.picker-field {
  display: flex;
  margin-bottom: 8px;

  .transparent {
    width: 24px;
    height: 24px;
    margin-top: 4px;
    margin-left: 8px;
    position: relative;
    cursor: pointer;

    &::after {
      content: '';
      width: 26px;
      height: 2px;
      position: absolute;
      top: 11px;
      left: -1px;
      transform: rotate(-45deg);
      background-color: #f00;
    }

    .checkerboard {
      background-size: auto;
    }
  }

  .straw {
    width: 24px;
    height: 24px;
    margin-top: 4px;
    margin-left: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    background-color: #f5f5f5;
    outline: 1px solid #f1f1f1;
    cursor: pointer;
  }
  .input {
    flex: 1;
  }
}

.picker-gradient-col {
  margin-right: 10px;
  margin-bottom: 10px;
  display: inline-block;
}
.picker-gradient-color {
  width: 40px;
  height: 40px;
  position: relative;
  cursor: pointer;
  border-radius: 20px;
}

.recent-colors-title {
  font-size: 12px;
  margin-bottom: 4px;
  margin-top: 10px;
}
</style>
